import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Upload,
  FileSpreadsheet,
  Download,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users,
  ArrowRight,
  Trash2,
  Eye
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REQUIRED_COLUMNS = [
  'business_name',
  'email'
];

const OPTIONAL_COLUMNS = [
  'contact_phone',
  'address',
  'subdomain',
  'password',
  'logo_url',
  'global_price_multiplier',
  'business_description'
];

export default function AdminImport() {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Results
  const fileInputRef = useRef(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
  });

  const downloadTemplate = () => {
    const headers = [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS];
    const sampleRow = [
      'Demo Store',
      'seller@example.com',
      'password123',
      '555-123-4567',
      '123 Main St, City, State 12345',
      'demostore',
      '',
      '2.5',
      'A sample store description'
    ];

    const csvContent = [
      headers.join(','),
      sampleRow.join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'reseller_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return { data: [], errors: ['File must have headers and at least one data row'] };

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
    const data = [];
    const errors = [];

    // Check required columns
    const missingColumns = REQUIRED_COLUMNS.filter(col => !headers.includes(col));
    if (missingColumns.length > 0) {
      errors.push(`Missing required columns: ${missingColumns.join(', ')}`);
      return { data: [], errors };
    }

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const row = {};

      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });

      // Validate required fields
      const rowErrors = [];
      if (!row.business_name) rowErrors.push('business_name is required');
      if (!row.email) rowErrors.push('email is required');
      if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
        rowErrors.push('invalid email format');
      }

      // Generate subdomain if not provided
      if (!row.subdomain && row.business_name) {
        row.subdomain = row.business_name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '')
          .substring(0, 20);
      }

      // Generate default password if not provided
      if (!row.password && row.subdomain) {
        row.password = `${row.subdomain}@123`;
      }

      // Set default multiplier
      if (!row.global_price_multiplier) {
        row.global_price_multiplier = '2.5';
      }

      row._rowNumber = i + 1;
      row._errors = rowErrors;
      row._valid = rowErrors.length === 0;

      data.push(row);
    }

    return { data, errors };
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setFile(selectedFile);

    const reader = new FileReader();
    reader.onload = (event) => {
      const { data, errors } = parseCSV(event.target.result);
      setParsedData(data);
      setValidationErrors(errors);
      setStep(2);
    };
    reader.readAsText(selectedFile);
  };

  const handleImport = async () => {
    const validRows = parsedData.filter(row => row._valid);
    if (validRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }

    setImporting(true);
    try {
      const response = await axios.post(`${API}/admin/import-resellers`, {
        resellers: validRows.map(row => ({
          business_name: row.business_name,
          email: row.email,
          password: row.password,
          contact_phone: row.contact_phone || '',
          address: row.address || '',
          subdomain: row.subdomain,
          logo_url: row.logo_url || '',
          global_price_multiplier: parseFloat(row.global_price_multiplier) || 2.5,
          business_description: row.business_description || ''
        }))
      }, getAuthHeaders());

      setImportResults(response.data);
      setStep(3);
      toast.success(`Successfully imported ${response.data.success_count} resellers!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setFile(null);
    setParsedData([]);
    setValidationErrors([]);
    setImportResults(null);
    setStep(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validCount = parsedData.filter(r => r._valid).length;
  const invalidCount = parsedData.filter(r => !r._valid).length;

  return (
    <div className="space-y-6" data-testid="admin-import">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Import Resellers
          </h1>
          <p className="text-slate-500">Bulk import resellers from CSV file</p>
        </div>
        <Button variant="outline" onClick={downloadTemplate} className="gap-2">
          <Download className="w-4 h-4" />
          Download Template
        </Button>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4">
        {[
          { num: 1, label: 'Upload CSV' },
          { num: 2, label: 'Preview & Validate' },
          { num: 3, label: 'Import Results' }
        ].map((s, i) => (
          <div key={s.num} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= s.num ? 'bg-amber-600 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
              {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
            </div>
            <span className={`ml-2 text-sm ${step >= s.num ? 'text-slate-900' : 'text-slate-400'}`}>
              {s.label}
            </span>
            {i < 2 && <ArrowRight className="w-4 h-4 mx-4 text-slate-300" />}
          </div>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="bg-white rounded-xl shadow-sm border p-8">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FileSpreadsheet className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Upload CSV File</h3>
            <p className="text-slate-500 mb-6">
              Upload a CSV file with your reseller data. Download the template for the correct format.
            </p>

            <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 hover:border-amber-400 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-600 mb-1">Click to upload or drag and drop</p>
                <p className="text-sm text-slate-400">CSV files only</p>
              </label>
            </div>

            <div className="mt-6 p-4 bg-slate-50 rounded-lg text-left">
              <p className="text-sm font-medium text-slate-700 mb-2">Required columns:</p>
              <div className="flex flex-wrap gap-2">
                {REQUIRED_COLUMNS.map(col => (
                  <Badge key={col} variant="secondary">{col}</Badge>
                ))}
              </div>
              <p className="text-sm font-medium text-slate-700 mt-3 mb-2">Optional columns:</p>
              <div className="flex flex-wrap gap-2">
                {OPTIONAL_COLUMNS.map(col => (
                  <Badge key={col} variant="outline">{col}</Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Preview */}
      {step === 2 && (
        <div className="space-y-4">
          {/* Validation Summary */}
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-700">{validCount} valid</span>
                </div>
                {invalidCount > 0 && (
                  <div className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-600" />
                    <span className="font-medium text-red-700">{invalidCount} with errors</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={resetImport}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={validCount === 0 || importing}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Users className="w-4 h-4 mr-2" />
                      Import {validCount} Resellers
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">File validation errors:</p>
                  <ul className="list-disc list-inside text-sm text-red-700 mt-1">
                    {validationErrors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Data Preview Table */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Row</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Business Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Subdomain</TableHead>
                    <TableHead>Price Multiplier</TableHead>
                    <TableHead>Errors</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedData.map((row, i) => (
                    <TableRow key={i} className={row._valid ? '' : 'bg-red-50'}>
                      <TableCell className="font-mono text-xs">{row._rowNumber}</TableCell>
                      <TableCell>
                        {row._valid ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{row.business_name}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell className="font-mono text-sm">{row.subdomain}</TableCell>
                      <TableCell>{row.global_price_multiplier}x</TableCell>
                      <TableCell>
                        {row._errors.length > 0 && (
                          <span className="text-xs text-red-600">
                            {row._errors.join(', ')}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && importResults && (
        <div className="bg-white rounded-xl shadow-sm border p-8">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Import Complete!</h3>

            <div className="flex justify-center gap-8 my-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">{importResults.success_count}</p>
                <p className="text-sm text-slate-500">Imported</p>
              </div>
              {importResults.failed_count > 0 && (
                <div className="text-center">
                  <p className="text-3xl font-bold text-red-600">{importResults.failed_count}</p>
                  <p className="text-sm text-slate-500">Failed</p>
                </div>
              )}
            </div>

            {importResults.errors && importResults.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left mb-6">
                <p className="font-medium text-red-800 mb-2">Import Errors:</p>
                <ul className="list-disc list-inside text-sm text-red-700">
                  {importResults.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={resetImport}>
                Import More
              </Button>
              <Button
                onClick={() => window.location.href = '/admin/resellers'}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Eye className="w-4 h-4 mr-2" />
                View Resellers
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
